import React, { useState, useEffect, useRef } from "react";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import FormDialog from "./FormDialog";
import { httpClient } from "appUtility/Api";
import {
  NotificationManager,
  NotificationContainer,
} from "react-notifications";
import moment from "moment";
import { DatePicker } from "material-ui-pickers";
import Button from "@material-ui/core/Button";
import { ResponsiveContainer } from "recharts";
import LinearBuffer from "./LinearBuffer";
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useTheme,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {
  KeyboardArrowRight,
  KeyboardArrowLeft,
  ArrowUpward,
  ArrowDownward,
  UnfoldMore,
} from "@material-ui/icons";
import PropTypes from "prop-types";

const qs = require("qs");
const loginData = JSON.parse(window.localStorage.getItem("login"));

// Add sort field mapping function
const getSortField = (columnId) => {
  switch (columnId) {
    case "date":
      return "addedDate";
    case "franchiseeName":
      return "franchiseeName";
    case "customer_name":
      return "firstName";
    case "phone":
      return "phone";
    case "address":
      return "address";
    case "suburb":
      return "suburb";
    case "callType":
      return "messageType";
    default:
      return columnId;
  }
};

const columnHead = [
  {
    id: "referenceNumber",
    disablePadding: "default",
    label: "Reference No.",
    sortable: true,
  },
  {
    id: "date",
    disablePadding: "default",
    label: "Added Date/Time",
    sortable: true,
  },
  {
    id: "franchiseeName",
    align: "left",
    disablePadding: "default",
    label: "Franchisee Name",
    sortable: true,
  },
  {
    id: "customer_name",
    align: "left",
    disablePadding: "default",
    label: "Customer",
    sortable: true,
  },
  {
    id: "phone",
    disablePadding: "default",
    label: "Phone",
    sortable: true,
  },
  {
    id: "address",
    disablePadding: "default",
    label: "Address",
    sortable: true,
  },
  {
    id: "suburb",
    disablePadding: "default",
    label: "Suburb",
    sortable: true,
  },
  {
    id: "callType",
    disablePadding: "default",
    label: "Call Type",
    sortable: true,
  },
  {
    id: "action",
    disablePadding: "default",
    label: "Actions",
    sortable: false,
  },
];

function TablePaginationActions(props) {
  const { count, page, rowsPerPage, onChangePage } = props;
  const theme = useTheme();

  const handleBackButtonClick = (event) => {
    onChangePage(event, parseInt(page) - 1);
  };

  const handleNextButtonClick = (event) => {
    onChangePage(event, parseInt(page) + 1);
  };

  return (
    <div style={{ display: "flex" }}>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page == 1}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage)}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
    </div>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onChangePage: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const EnhancedTableHead = ({ sortBy, sortDirection, onRequestSort }) => {
  return (
    <TableHead>
      <TableRow>
        {columnHead.map((column) => {
          const sortField = getSortField(column.id);
          const isSorted = sortBy === sortField;

          return (
            <TableCell
              key={column.id}
              align={column.align}
              padding={column.disablePadding}
              style={{
                cursor: column.sortable ? "pointer" : "default",
                fontWeight: "bold",
              }}
              onClick={() => column.sortable && onRequestSort(column.id)}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    column.align === "center" ? "center" : "flex-start",
                }}
              >
                {column.label}
                {column.sortable && (
                  <span style={{ marginLeft: 4 }}>
                    {isSorted ? (
                      sortDirection === "asc" ? (
                        <ArrowUpward fontSize="small" />
                      ) : (
                        <ArrowDownward fontSize="small" />
                      )
                    ) : (
                      <UnfoldMore fontSize="small" style={{ opacity: 0.5 }} />
                    )}
                  </span>
                )}
              </div>
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "2px 4px",
    display: "flex",
    alignItems: "center",
    width: 400,
  },
  input: {
    marginLeft: theme.spacing(1),
    flex: 1,
  },
  iconButton: {
    padding: 10,
  },
  divider: {
    height: 28,
    margin: 4,
  },
  dateContainer: {
    display: "flex",
    alignItems: "center",
  },
  clearButton: {
    marginLeft: theme.spacing(1),
  },
}));

const EnhancedTable = () => {
  const classes = useStyles();

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState("addedDate");
  const [sortDirection, setSortDirection] = useState("desc");
  const [loading, setLoading] = useState(false);

  // Track which filters are currently applied
  const [appliedFilters, setAppliedFilters] = useState({
    start_date: null,
    end_date: null,
    franchiseName: "",
    callType: "",
    search: "",
  });

  // Current state of filter inputs
  const [filterState, setFilterState] = useState({
    start_date: null,
    end_date: null,
    franchiseName: "",
    callType: "",
    search: "",
  });

  const [tableState, setTableState] = useState({
    order: "asc",
    orderBy: "_id",
    selected: [],
    getCallQuery: {
      userID: loginData && loginData.uid,
      userCode: loginData && loginData.aCode,
    },
    callerData: [],
    page: 1,
    rowsPerPage: 20,
  });

  const [dateCheck, setDateCheck] = useState(false);
  const [filterCheck, setFilterCheck] = useState(false);

  const [searchMode, setSearchMode] = useState(false);
  const [franchiseList, setFranchiseList] = useState([]);

  // Track when filters are being removed
  const filterRemovalRef = useRef(false);

  // Fetch data function
  const fetchData = () => {
    setLoading(true);
    setFilterCheck(false);
    setTableState((prev) => ({ ...prev, callerData: [] }));

    let params = {
      userID: loginData.uid,
      userCode: loginData.aCode,
      page: page,
      per_page: rowsPerPage,
      sort_by: sortBy,
      direction: sortDirection,
    };

    // Only apply filters if in search mode
    if (searchMode) {
      Object.assign(params, {
        start_date: appliedFilters.start_date
          ? moment(appliedFilters.start_date).format("YYYY-MM-DD")
          : null,
        end_date: appliedFilters.end_date
          ? moment(appliedFilters.end_date).format("YYYY-MM-DD")
          : null,
        franchiseeName: appliedFilters.franchiseName,
        search: appliedFilters.search,
        messageType: appliedFilters.callType,
      });
    }

    httpClient
      .post(
        "getCallLog2.php",
        qs.stringify({
          action: "call_log",
          source: params,
        })
      )
      .then((res) => {
        setLoading(false);
        if (res.data === null) {
          NotificationManager.warning("Data not available right now");
        } else {
          if (!res.data.data || res.data.data.length === 0) {
            setFilterCheck(true);
          } else {
            setFilterCheck(false);
          }

          const modifiedData =
            res.data.data.map((item) => ({
              ...item,
              messageType: item.endCall === "1" ? "end_call" : item.messageType,
              callScenario: item.endCall === "1" ? item.messageType : "",
            })) || [];

          setTableState((prev) => ({
            ...prev,
            callerData: modifiedData,
          }));
          setTotal(res.data.meta.total || 0);
          setPage(res.data.meta.current_page || 1);
          setRowsPerPage(res.data.meta.per_page || 20);
          setFrom(res.data.meta.from || 0);
          setTo(res.data.meta.to || 0);
        }
      })
      .catch((error) => {
        setLoading(false);
        NotificationManager.error("Error fetching data");
        console.error("API Error:", error);
      });
  };

  // Fetch data when dependencies change
  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, sortBy, sortDirection, appliedFilters]);

  useEffect(() => {
    getAllFranchise();
    // Initial data fetch
    fetchData();
  }, []);

  const getAllFranchise = () => {
    const params = {
      userID: loginData && loginData.uid,
      userCode: loginData && loginData.aCode,
      appID: loginData && loginData.aid,
    };
    httpClient
      .post(
        "getAllFranchisee.php",
        qs.stringify({
          action: "all_franchisee",
          source: params,
        })
      )
      .then(({ data }) => {
        if (data) {
          setFranchiseList(data.data);
        }
      });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(1);
  };

  const handleSearchQuery = (e) => {
    const newValue = e.target.value;
    setFilterState((prev) => ({ ...prev, search: newValue }));

    // If search field is cleared and it was previously applied
    if (newValue === "" && appliedFilters.search !== "") {
      filterRemovalRef.current = true;
      setAppliedFilters((prev) => ({ ...prev, search: "" }));
    }
  };

  const applyFilters = () => {
    // Check if we're removing a filter
    if (filterRemovalRef.current) {
      filterRemovalRef.current = false;
      return;
    }

    // Check if any filter is active
    const isActive = Object.values(filterState).some(
      (val) => val !== null && val !== ""
    );

    setSearchMode(isActive);
    setAppliedFilters(filterState);
    setPage(1);
  };

  const resetFilters = () => {
    const resetState = {
      start_date: null,
      end_date: null,
      franchiseName: "",
      callType: "",
      search: "",
    };

    setFilterState(resetState);
    setAppliedFilters(resetState);
    setSearchMode(false);
    setPage(1);
  };

  const handleFromDate = (info) => {
    setFilterState((prev) => ({ ...prev, start_date: info }));

    // If start date is cleared and it was previously applied
    if (info === null && appliedFilters.start_date !== null) {
      filterRemovalRef.current = true;
      setAppliedFilters((prev) => ({ ...prev, start_date: null }));
    }
  };

  const handleToDate = (info) => {
    setFilterState((prev) => ({ ...prev, end_date: info }));

    // If end date is cleared and it was previously applied
    if (info === null && appliedFilters.end_date !== null) {
      filterRemovalRef.current = true;
      setAppliedFilters((prev) => ({ ...prev, end_date: null }));
    }
  };

  const clearStartDate = () => {
    setFilterState((prev) => ({ ...prev, start_date: null }));

    // If start date was applied, trigger removal
    if (appliedFilters.start_date !== null) {
      filterRemovalRef.current = true;
      setAppliedFilters((prev) => ({ ...prev, start_date: null }));
    }
  };

  const clearEndDate = () => {
    setFilterState((prev) => ({ ...prev, end_date: null }));

    // If end date was applied, trigger removal
    if (appliedFilters.end_date !== null) {
      filterRemovalRef.current = true;
      setAppliedFilters((prev) => ({ ...prev, end_date: null }));
    }
  };

  const handleChangeCallType = (e) => {
    const newValue = e.target.value;
    setFilterState((prev) => ({ ...prev, callType: newValue }));

    // If call type is cleared and it was previously applied
    if (newValue === "" && appliedFilters.callType !== "") {
      filterRemovalRef.current = true;
      setAppliedFilters((prev) => ({ ...prev, callType: "" }));
    }
  };

  const handleFranchiseeChange = (event, value) => {
    const newValue = value ? value.franchiseeID : "";
    setFilterState((prev) => ({ ...prev, franchiseName: newValue }));

    // If franchisee is cleared and it was previously applied
    if (newValue === "" && appliedFilters.franchiseName !== "") {
      filterRemovalRef.current = true;
      setAppliedFilters((prev) => ({ ...prev, franchiseName: "" }));
    }
  };

  // Sort handler
  const handleRequestSort = (property) => {
    const sortField = getSortField(property);
    const isAsc = sortBy === sortField && sortDirection === "asc";
    const newDirection = isAsc ? "desc" : "asc";

    setSortBy(sortField);
    setSortDirection(newDirection);
    setPage(1);
  };

  // Check if any filter is active in the UI
  const isFilterActive = () => {
    return (
      filterState.start_date !== null ||
      filterState.end_date !== null ||
      filterState.callType !== "" ||
      filterState.franchiseName !== "" ||
      filterState.search !== ""
    );
  };

  return (
    <ResponsiveContainer>
      <div>
        <h2 className="title pb-3 pt-3 pl-4 font-weight-bold">List of calls</h2>
        <div>
          <div
            className="page-heading d-sm-flex flex-wrap align-items-sm-center"
            style={{
              width: "96%",
            }}
          >
            <div
              className="from-to-date-wrapper row"
              style={{
                width: "100%",
              }}
            >
              <div className="col-md-6 start-date mb-3">
                <h3 style={{ color: "#8f8f8f", fontWeight: "bold" }}>From</h3>
                <div className={classes.dateContainer}>
                  <DatePicker
                    value={filterState.start_date}
                    onChange={handleFromDate}
                    animateYearScrolling={false}
                    leftArrowIcon={<i className="zmdi zmdi-arrow-back" />}
                    rightArrowIcon={<i className="zmdi zmdi-arrow-forward" />}
                    errorText={dateCheck}
                    variant="outlined"
                    style={{ width: "100%" }}
                  />
                  {filterState.start_date && (
                    <IconButton
                      className={classes.clearButton}
                      onClick={clearStartDate}
                      style={{
                        position: "absolute",
                        right: "15px",
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </div>
                <div>
                  {dateCheck && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "rgb(244, 67, 54)",
                      }}
                    >
                      Please enter the Start Date !
                    </div>
                  )}
                </div>
              </div>

              <div className="col-md-6 end-date mb-3">
                <h3 style={{ color: "#8f8f8f", fontWeight: "bold" }}>To</h3>
                <div className={classes.dateContainer}>
                  <DatePicker
                    value={filterState.end_date}
                    onChange={handleToDate}
                    animateYearScrolling={false}
                    leftArrowIcon={<i className="zmdi zmdi-arrow-back" />}
                    rightArrowIcon={<i className="zmdi zmdi-arrow-forward" />}
                    errorText={dateCheck}
                    variant="outlined"
                    style={{ width: "100%" }}
                  />
                  {filterState.end_date && (
                    <IconButton
                      className={classes.clearButton}
                      onClick={clearEndDate}
                      style={{
                        position: "absolute",
                        right: "15px",
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </div>
                <div>
                  {dateCheck && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "rgb(244, 67, 54)",
                      }}
                    >
                      Please enter the end Date !
                    </div>
                  )}
                </div>
              </div>

              <div className="col-md-4">
                <InputLabel className="font-weight-bold mb-2">
                  Select Call Type
                </InputLabel>
                <FormControl fullWidth style={{ width: "100%" }}>
                  <Select
                    value={filterState.callType}
                    variant="outlined"
                    onChange={handleChangeCallType}
                  >
                    <MenuItem value={""}>
                      <em>select call type</em>
                    </MenuItem>
                    <MenuItem value={"Service_Required"}>
                      Service Required
                    </MenuItem>
                    <MenuItem value={"Cancel_Booking"}>Cancel Booking</MenuItem>
                    <MenuItem value={"Message_for_operator"}>
                      Message for operator
                    </MenuItem>
                    <MenuItem value={"Franchise_Enquiry"}>
                      Franchise Enquiry
                    </MenuItem>
                    <MenuItem value={"Advertising_Enquiry"}>
                      Advertising Enquiry
                    </MenuItem>
                    <MenuItem value={"Complaint"}>Complaint</MenuItem>
                    <MenuItem value={"Declined_to_Proceed"}>
                      Declined to Proceed
                    </MenuItem>
                    <MenuItem value={"end_call"}>End Call</MenuItem>
                  </Select>
                </FormControl>
              </div>

              <div className="col-md-4">
                <InputLabel className="font-weight-bold mb-2">
                  Select Franchisee
                </InputLabel>
                <Autocomplete
                  options={franchiseList}
                  getOptionLabel={(option) => option.franchiseeName}
                  value={
                    franchiseList.find(
                      (f) => f.franchiseeID === filterState.franchiseName
                    ) || null
                  }
                  onChange={handleFranchiseeChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Search franchisee"
                    />
                  )}
                  filterOptions={(options, state) => {
                    return options.filter((option) =>
                      option.franchiseeName
                        .toLowerCase()
                        .includes(state.inputValue.toLowerCase())
                    );
                  }}
                />
              </div>

              <div className="col-md-4">
                <InputLabel className="font-weight-bold mb-2">
                  Search Call Log
                </InputLabel>
                <TextField
                  style={{ width: "100%" }}
                  variant="outlined"
                  placeholder="Search Call Log"
                  name="search"
                  value={filterState.search}
                  onChange={handleSearchQuery}
                />
              </div>
              <div className="col-md-12 mt-3">
                <div style={{ float: "right" }}>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={applyFilters}
                    disabled={!isFilterActive()}
                  >
                    Search
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {searchMode ? (
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            <h2
              style={{
                marginLeft: "3%",
                fontWeight: "600",
              }}
            >
              Latest Search Results
            </h2>

            <Button
              variant="contained"
              color="primary"
              className="jr-btn text-white"
              style={{
                marginLeft: "5%",
              }}
              onClick={resetFilters}
            >
              See all Calls
            </Button>
          </div>
        ) : null}

        <div className="flex-auto" style={{ marginTop: "2%" }}>
          <div
            className="table-responsive-material"
            style={{ marginLeft: "2%", marginRight: "2%" }}
          >
            <Table>
              <EnhancedTableHead
                sortBy={sortBy}
                sortDirection={sortDirection}
                onRequestSort={handleRequestSort}
              />

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <LinearBuffer />
                    </TableCell>
                  </TableRow>
                ) : tableState.callerData &&
                  tableState.callerData.length > 0 ? (
                  tableState.callerData.map((each, index) => {
                    return (
                      <TableRow tabIndex={-1} key={index}>
                        <TableCell padding="default">
                          {each.referenceNumber}
                        </TableCell>
                        <TableCell padding="default">
                          {each.addedDate == "0000-00-00" ||
                          each.addedDate == ""
                            ? "-"
                            : moment(
                                each.addedDate + " " + each.addedTime
                              ).format("Do MMM YYYY, h:mm a")}
                        </TableCell>
                        <TableCell padding="default">
                          {each.franchiseeName}
                        </TableCell>
                        <TableCell padding="default">
                          {each.firstName} {each.lastName}
                        </TableCell>
                        <TableCell padding="default">
                          {each.phone && each.phone2
                            ? each.phone + " / " + each.phone2
                            : each.phone || each.phone2}
                        </TableCell>
                        <TableCell padding="default">{each.address}</TableCell>
                        <TableCell padding="default">{each.suburb}</TableCell>
                        <TableCell
                          padding="default"
                          style={{ color: "#b5b5b5" }}
                        >
                          {each.messageType == "" ? "-" : each.messageType}
                        </TableCell>
                        <TableCell padding="default">
                          <FormDialog
                            rowID={each.logID}
                            rowDate={each.addedDate}
                            rowTime={each.addedTime}
                            rowPhone={each.phone}
                            rowPhone2={each.phone2}
                            rowSuburb={each.suburb}
                            rowFirstName={each.firstName}
                            rowLastName={each.lastName}
                            rowAddress={each.address}
                            rowPetName={each.petName}
                            rowPetBreed={each.petBreed}
                            rowPetGender={each.gender}
                            interestedService={each.interestedService}
                            messageType={each.messageType}
                            rowEmail={each.email}
                            notes={each.notes}
                            leadNotes={each.leadNotes}
                            commentText={each.commentText || each.message}
                            reschedule={each.reschedule}
                            addedBy={each.addedBy}
                            referredby={each.referredby}
                            franchiseeName={each.franchiseeName}
                            referenceNumber={each.referenceNumber}
                            organization={each.organization}
                            callScenario={each.callScenario}
                            postCode={each.service_area_postcode}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Box
                        style={{
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        {filterCheck
                          ? "No Records Found!"
                          : "No data available"}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[20, 50, 100]}
                    count={total}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    labelDisplayedRows={() => `${from} - ${to} of ${total}`}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        </div>
        <NotificationContainer />
      </div>
    </ResponsiveContainer>
  );
};

export default EnhancedTable;
